import { CheckCircle2 } from 'lucide-react';
import type { StepDescriptor } from './dimensions';
import type { StepStatus } from '../../lib/store/wizard';
import { de } from '../../lib/i18n/de';

export type ModuleDisplayStatus = StepStatus | 'coming-soon' | 'locked';

interface DimensionModuleCardProps {
  step: StepDescriptor;
  status: ModuleDisplayStatus;
  onClick: () => void;
}

/**
 * Magazin-Card pro Dimension. Adaptiert vom Gründerworkspace-Pattern, mit
 * folgenden Anpassungen für die Tragfähigkeits-App:
 *
 * - Pastell-Akzentblock nutzt die jeweilige Dimensionsfarbe als CSS-Variable
 *   mit 40 % Opacity (statt der drei festen rose/violet/sky-Tints aus V2). Das
 *   verbindet die sechs Module visuell mit dem Brand-Token-System.
 * - Status-Labels in Sie-Form, behördentauglich („Modul starten" statt
 *   „Los geht's").
 * - Triage hat Status-Variante 'locked' für nichts mehr, weil sie vom Hub aus
 *   immer initial 'available' ist.
 */
export function DimensionModuleCard({ step, status, onClick }: DimensionModuleCardProps) {
  const isComplete = status === 'complete';
  const isLocked = status === 'locked';
  const isComingSoon = status === 'coming-soon';
  const isDisabled = isLocked || isComingSoon;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      aria-label={`${step.name} – ${statusToLabel(status)}`}
      className={`group relative w-full border-t border-stone-200 py-8 text-left transition-colors duration-300 md:py-10 ${
        isDisabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-stone-50/60'
      } focus-visible:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stone-900`}
    >
      <div className="flex items-start gap-6 md:gap-10">
        {/* Große Modul-Nummer in Source Serif Black */}
        <span
          aria-hidden="true"
          className={`select-none font-serif text-5xl font-black leading-none transition-colors duration-300 md:text-8xl ${
            isComplete ? 'text-emerald-200' : 'text-stone-200'
          } ${!isDisabled && !isComplete ? 'group-hover:text-stone-300' : ''}`}
        >
          {step.nummer}
        </span>

        {/* Inhalt */}
        <div className="min-w-0 flex-1 pt-1 md:pt-2">
          <h3 className="font-serif text-lg font-bold uppercase leading-tight tracking-wide text-stone-900 md:text-xl">
            {step.name}
          </h3>
          <p className="mt-1 font-serif text-sm italic text-stone-500 md:text-base">
            {step.subtitle}
          </p>

          {/* Status-Label */}
          <div className="mt-3">
            <StatusBadge status={status} />
          </div>
        </div>

        {/* Pastell-Akzentblock in Dimensionsfarbe */}
        <div
          aria-hidden="true"
          className="hidden h-14 w-14 flex-shrink-0 self-center rounded-sm transition-transform duration-500 group-hover:rotate-3 group-hover:scale-110 md:block"
          style={{
            backgroundColor: step.accentHex,
            opacity: isDisabled ? 0.2 : 0.45,
          }}
        />
      </div>
    </button>
  );
}

function StatusBadge({ status }: { status: ModuleDisplayStatus }) {
  if (status === 'complete') {
    return (
      <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium uppercase tracking-widest text-emerald-600">
        <CheckCircle2 size={14} aria-hidden="true" />
        {de.status.complete}
      </span>
    );
  }
  if (status === 'untouched') {
    return (
      <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-900 group-hover:underline">
        {de.status.available} &rarr;
      </span>
    );
  }
  if (status === 'in-progress') {
    return (
      <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-600 group-hover:underline">
        {de.status.inProgress} &rarr;
      </span>
    );
  }
  if (status === 'locked') {
    return (
      <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-300">
        {de.status.locked}
      </span>
    );
  }
  // coming-soon
  return (
    <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-300">
      {de.status.comingSoon}
    </span>
  );
}

function statusToLabel(status: ModuleDisplayStatus): string {
  switch (status) {
    case 'complete':
      return de.status.complete;
    case 'in-progress':
      return de.status.inProgress;
    case 'locked':
      return de.status.locked;
    case 'coming-soon':
      return de.status.comingSoon;
    default:
      return de.status.available;
  }
}
